import React, { useState, useEffect, useRef } from 'react';
import { enrollmentAPI } from '../services/api';
import './EnrollForm.css';

const EnrollForm = () => {
  const [formData, setFormData] = useState({
    studentName: '', fatherName: '', motherName: '', dateOfBirth: '',
    gender: '', applyingFor: '', previousSchool: '', address: '',
    phone: '', email: '', bloodGroup: '', guardianPhone: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const processRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.15 }
    );
    const steps = processRef.current?.querySelectorAll('.process-step');
    steps?.forEach((s) => observer.observe(s));
    return () => steps?.forEach((s) => observer.unobserve(s));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const apiData = {
        student_name: formData.studentName,
        father_name: formData.fatherName,
        mother_name: formData.motherName || null,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        blood_group: formData.bloodGroup || null,
        applying_for_class: formData.applyingFor,
        previous_school: formData.previousSchool || null,
        address: formData.address,
        phone: formData.phone,
        email: formData.email || null,
        guardian_phone: formData.guardianPhone || null
      };
      const response = await enrollmentAPI.submitApplication(apiData);
      setApplicationId(response.id);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setApplicationId(null);
        setFormData({
          studentName: '', fatherName: '', motherName: '', dateOfBirth: '',
          gender: '', applyingFor: '', previousSchool: '', address: '',
          phone: '', email: '', bloodGroup: '', guardianPhone: ''
        });
      }, 5000);
    } catch (err) {
      console.error('Enrollment error:', err);
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const admissionSteps = [
    { number: '01', icon: '📝', title: 'Fill Online Form', desc: 'Complete the enrollment form below with student and guardian details.' },
    { number: '02', icon: '📄', title: 'Submit Documents', desc: 'Provide required documents — birth certificate, photos, CNIC, and previous reports.' },
    { number: '03', icon: '✍️', title: 'Admission Test', desc: 'Student appears for a short assessment to determine the right academic level.' },
    { number: '04', icon: '📋', title: 'Review & Offer', desc: 'Our admissions team reviews the application and sends an offer letter.' },
    { number: '05', icon: '💳', title: 'Fee Payment', desc: 'Complete the fee payment at the school office or via bank transfer.' },
    { number: '06', icon: '🎓', title: 'Welcome to MTC!', desc: 'Collect your uniform, books, and ID card — your journey begins!' },
  ];

  return (
    <section id="enroll" className="enroll-section">
      <div className="container">
        <div className="section-title">
          <h2>Admissions Open</h2>
          <p>Join MTC School and start your journey towards excellence</p>
        </div>

        {/* ── Admission Process Timeline ── */}
        <div className="admission-process">
          <div className="process-header">
            <span className="process-badge">How It Works</span>
            <h3>Admission Process</h3>
            <p>Follow these simple steps to enroll your child at MTC School</p>
          </div>
          <div className="process-timeline" ref={processRef}>
            {admissionSteps.map((step, i) => (
              <div className="process-step" key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="step-marker">
                  <span className="step-number">{step.number}</span>
                </div>
                <div className="step-body">
                  <span className="step-icon">{step.icon}</span>
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Form + Sidebar ── */}
        <div className="enroll-grid">
          {/* Sidebar */}
          <aside className="enroll-sidebar">
            <div className="sidebar-card documents-card">
              <div className="sidebar-card-header">
                <span className="sidebar-icon">📄</span>
                <h4>Required Documents</h4>
              </div>
              <ul className="checklist">
                <li><span className="check">✓</span> Birth Certificate (original & copy)</li>
                <li><span className="check">✓</span> Previous School Report Card</li>
                <li><span className="check">✓</span> 4 Passport-size Photographs</li>
                <li><span className="check">✓</span> Parent/Guardian CNIC Copy</li>
                <li><span className="check">✓</span> School Leaving Certificate</li>
                <li><span className="check">✓</span> Medical Fitness Certificate</li>
              </ul>
            </div>
            <div className="sidebar-card contact-card">
              <div className="sidebar-card-header">
                <span className="sidebar-icon">📞</span>
                <h4>Admission Office</h4>
              </div>
              <div className="contact-details">
                <div className="contact-row">
                  <i className="fas fa-phone"></i>
                  <div>
                    <span className="contact-label">Phone</span>
                    <span className="contact-value">+92 336 3012806</span>
                  </div>
                </div>
                <div className="contact-row">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <span className="contact-label">Email</span>
                    <span className="contact-value">info@mtcschool.edu</span>
                  </div>
                </div>
                <div className="contact-row">
                  <i className="fas fa-clock"></i>
                  <div>
                    <span className="contact-label">Office Hours</span>
                    <span className="contact-value">Mon – Sat: 8:00 AM – 2:00 PM</span>
                  </div>
                </div>
                <div className="contact-row">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <span className="contact-label">Location</span>
                    <span className="contact-value">Satellite Town, Mirpurkhas</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="enroll-form-wrapper">
            {submitted ? (
              <div className="success-state">
                <div className="success-icon-wrap">
                  <span className="success-check">✓</span>
                </div>
                <h3>Application Submitted Successfully!</h3>
                <p>Thank you for applying to MTC School. Our admissions team will contact you within 2–3 business days.</p>
                {applicationId && (
                  <div className="application-id-badge">
                    Application ID: <strong>#{applicationId}</strong>
                  </div>
                )}
              </div>
            ) : (
              <form className="enroll-form" onSubmit={handleSubmit}>
                <div className="form-header">
                  <h3>Enrollment Form</h3>
                  <p>Fields marked with <span className="required-mark">*</span> are required</p>
                </div>

                {error && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i> {error}
                  </div>
                )}

                {/* Student Information */}
                <fieldset className="form-fieldset">
                  <legend>Student Information</legend>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Student Name <span className="req">*</span></label>
                      <input type="text" name="studentName" value={formData.studentName} onChange={handleChange} required placeholder="Full name" disabled={loading} />
                    </div>
                    <div className="form-group">
                      <label>Date of Birth <span className="req">*</span></label>
                      <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required disabled={loading} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Gender <span className="req">*</span></label>
                      <select name="gender" value={formData.gender} onChange={handleChange} required>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Blood Group</label>
                      <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option><option value="A-">A-</option>
                        <option value="B+">B+</option><option value="B-">B-</option>
                        <option value="AB+">AB+</option><option value="AB-">AB-</option>
                        <option value="O+">O+</option><option value="O-">O-</option>
                      </select>
                    </div>
                  </div>
                </fieldset>

                {/* Academic Details */}
                <fieldset className="form-fieldset">
                  <legend>Academic Details</legend>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Applying For <span className="req">*</span></label>
                      <select name="applyingFor" value={formData.applyingFor} onChange={handleChange} required>
                        <option value="">Select Class or Program</option>
                        <optgroup label="School Classes">
                          <option value="Play Group">Play Group</option>
                          <option value="Nursery">Nursery</option>
                          <option value="Prep">Prep</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={i + 1} value={`Class ${i + 1}`}>Class {i + 1}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Coaching Center">
                          <option value="Coaching - MCAT">M-CAT Preparation</option>
                          <option value="Coaching - ECAT">E-CAT Preparation</option>
                          <option value="Coaching - ICS">ICS Program</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={`c${i + 1}`} value={`Coaching - Class ${i + 1}`}>Class {i + 1}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Previous School / College</label>
                      <input type="text" name="previousSchool" value={formData.previousSchool} onChange={handleChange} placeholder="Institution name (if any)" />
                    </div>
                  </div>
                </fieldset>

                {/* Parent / Guardian */}
                <fieldset className="form-fieldset">
                  <legend>Parent / Guardian</legend>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Father's Name <span className="req">*</span></label>
                      <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} required placeholder="Father's full name" disabled={loading} />
                    </div>
                    <div className="form-group">
                      <label>Mother's Name</label>
                      <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} placeholder="Mother's full name" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone Number <span className="req">*</span></label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+92 XXX XXXXXXX" />
                    </div>
                    <div className="form-group">
                      <label>Guardian's Phone</label>
                      <input type="tel" name="guardianPhone" value={formData.guardianPhone} onChange={handleChange} placeholder="+92 XXX XXXXXXX" />
                    </div>
                  </div>
                </fieldset>

                {/* Contact Details */}
                <fieldset className="form-fieldset">
                  <legend>Contact Details</legend>
                  <div className="form-group full-width">
                    <label>Address <span className="req">*</span></label>
                    <textarea name="address" value={formData.address} onChange={handleChange} required placeholder="Complete residential address" rows="2"></textarea>
                  </div>
                  <div className="form-group full-width">
                    <label>Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" disabled={loading} />
                  </div>
                </fieldset>

                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin"></i> Submitting Application...</>
                  ) : (
                    <><i className="fas fa-paper-plane"></i> Submit Application</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnrollForm;
